import { EventBus } from '~/rage/common';
import { notifyOnError } from '~/rage/decorators';
import {
	type IAdminAnticheatPlayerOffline,
	type IAdminAnticheatRequests,
	type IAdminMarkModerationRequestsViewed,
	type IAdminNicknameRequests,
	type IAdminNumberplateRequests,
	type IAdminPendingRequests,
	type IAdminRequestCount,
} from '~/shared';
import { IObservable, Subject } from '~/rage/utils';
import { BackendService } from '../BackendService';
import type { IRpcService } from '../RpcService';
import { normalizeScreenshotBmp } from '../ScreenshotBmp';
import { VarsService } from '../VarsService';
import {
	IAdminService,
	IAnticheatRequest,
	INicknameRequest,
	INumberplateRequest,
} from './adminServiceTypes';

export class AdminService implements IAdminService {
	private reportsCount = new Subject<number>(0);
	private moderationsCount = new Subject<number>(0);
	private anticheatCount = new Subject<number>(0);
	private numberplateRequests = new Subject<INumberplateRequest[]>([]);
	private nicknameRequests = new Subject<INicknameRequest[]>([]);
	private anticheatRequests = new Subject<IAnticheatRequest[]>([]);
	private viewedAnticheatRequestIds = new Set<string>();
	private screenshotCapture?: string;

	constructor(
		private readonly backend: BackendService,
		private readonly rpc: IRpcService,
		private readonly vars: VarsService,
	) {
		this.rpc.register(
			'admin:captureScreenshot',
			({ captureId }: { captureId: string }) => {
				void this.captureScreenshot(captureId);
			},
		);
		this.backend.addEventHandler(
			'admin:reportsCount',
			(data: IAdminRequestCount) => {
				this.reportsCount.next(data.count);
			},
		);

		this.backend.addEventHandler(
			'admin:moderationsCount',
			(data: IAdminRequestCount) => {
				this.moderationsCount.next(data.count);
			},
		);

		this.backend.addEventHandler(
			'admin:numberplateRequests',
			(data: IAdminNumberplateRequests) => {
				this.numberplateRequests.next(data.requests);
			},
		);

		this.backend.addEventHandler(
			'admin:nicknameRequests',
			(data: IAdminNicknameRequests) => {
				this.nicknameRequests.next(data.requests);
			},
		);

		this.backend.addEventHandler(
			'admin:anticheatRequests',
			(data: IAdminAnticheatRequests) => {
				const incomingIds = new Set(data.requests.map(({ id }) => id));
				this.anticheatRequests.next(
					[
						...data.requests,
						...this.anticheatRequests
							.getValue()
							.filter(({ id }) => !incomingIds.has(id)),
					].map((request, index) => ({
						...request,
						requestNumber: index + 1,
					})),
				);
				this.syncAnticheatCount();
			},
		);

		this.backend.addEventHandler(
			'admin:anticheatPlayerOffline',
			({ characterId }: IAdminAnticheatPlayerOffline) => {
				this.anticheatRequests.next(
					this.anticheatRequests
						.getValue()
						.map((request) =>
							request.characterId === characterId
								? { ...request, online: false }
								: request,
						),
				);
			},
		);

		this.backend.addEventHandler('admin:anticheatReset', () => {
			this.anticheatRequests.next([]);
			this.viewedAnticheatRequestIds.clear();
			this.syncAnticheatCount();
		});
	}

	public getReportsCount(): IObservable<number> {
		return this.reportsCount;
	}

	public getModerationsCount(): IObservable<number> {
		return this.moderationsCount;
	}

	public getAnticheatCount(): IObservable<number> {
		return this.anticheatCount;
	}

	public getNumberplateRequests(): IObservable<INumberplateRequest[]> {
		return this.numberplateRequests;
	}

	public getNicknameRequests(): IObservable<INicknameRequest[]> {
		return this.nicknameRequests;
	}

	public getAnticheatRequests(): IObservable<IAnticheatRequest[]> {
		return this.anticheatRequests;
	}

	public getScreenshot(): IObservable<string | null> {
		return this.vars.get('admin:screenshot', null);
	}

	@notifyOnError
	public async closeScreenshot(): Promise<void> {
		await this.rpc.invokeClient('admin:closeScreenshot');
	}

	public async approveNumberplateRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		await this.backend.invoke('admin:approveNumberplateRequest', {
			requestId,
			reason,
		});
	}

	public async rejectNumberplateRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		await this.backend.invoke('admin:rejectNumberplateRequest', {
			requestId,
			reason,
		});
	}

	public async approveNicknameRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		await this.backend.invoke('admin:approveNicknameRequest', {
			requestId,
			reason,
		});
	}

	public async rejectNicknameRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		await this.backend.invoke('admin:rejectNicknameRequest', {
			requestId,
			reason,
		});
	}

	@notifyOnError
	public async spectateAnticheatRequest(characterId: string): Promise<void> {
		await this.rpc.invokeClient('admin:spectateAnticheatRequest', {
			characterId,
		});
	}

	@notifyOnError
	public async closePanel(): Promise<void> {
		await this.rpc.invokeClient('admin:closePanel');
	}

	@notifyOnError
	public async fetchRequests(): Promise<void> {
		const data = await this.backend.invoke<IAdminPendingRequests>(
			'admin:getPendingRequests',
			{},
		);
		this.numberplateRequests.next(data.numberplateRequests);
		this.nicknameRequests.next(data.nicknameRequests);
		this.moderationsCount.next(data.moderationsCount);
	}

	@notifyOnError
	public async markAnticheatRequestsViewed(
		requestIds: string[],
	): Promise<void> {
		requestIds.forEach((requestId) =>
			this.viewedAnticheatRequestIds.add(requestId),
		);
		this.syncAnticheatCount();
	}

	private syncAnticheatCount(): void {
		this.anticheatCount.next(
			this.anticheatRequests
				.getValue()
				.filter(({ id }) => !this.viewedAnticheatRequestIds.has(id)).length,
		);
	}

	private async captureScreenshot(captureId: string): Promise<void> {
		if (this.screenshotCapture) {
			EventBus.triggerClient('admin:completeScreenshot', {
				captureId,
				error: 'Снимок экрана уже создаётся',
			});
			return;
		}

		const controller = new AbortController();
		this.screenshotCapture = captureId;
		let timeout: ReturnType<typeof setTimeout>;
		const deadline = new Promise<never>((_resolve, reject) => {
			timeout = setTimeout(() => {
				controller.abort();
				reject(new Error('Screenshot capture timed out'));
			}, 5000);
		});
		try {
			const dataUrl = await Promise.race([
				this.readScreenshot(controller.signal),
				deadline,
			]);
			if (controller.signal.aborted)
				throw new Error('Screenshot capture timed out');
			EventBus.triggerClient('admin:completeScreenshot', {
				captureId,
				dataUrl,
			});
		} catch (error) {
			const reason = String(error).slice(0, 200);
			console.error(`[Screenshot] capture=${captureId}: ${reason}`);
			EventBus.triggerClient('admin:completeScreenshot', {
				captureId,
				error: reason,
			});
		} finally {
			clearTimeout(timeout!);
			if (this.screenshotCapture === captureId)
				this.screenshotCapture = undefined;
		}
	}

	private async readScreenshot(signal: AbortSignal): Promise<string> {
		const response = await fetch('http://screenshots/take_with_cef', {
			cache: 'no-store',
			signal,
		});
		if (!response.ok)
			throw new Error(`Native screenshot: HTTP ${response.status}`);
		const blob = await normalizeScreenshotBmp(await response.blob());
		if (!blob.size) throw new Error('Native screenshot is empty');
		let image: ImageBitmap;
		try {
			image = await createImageBitmap(blob);
		} catch {
			const bytes = new Uint8Array(
				await blob.slice(0, blob.type === 'image/bmp' ? 38 : 16).arrayBuffer(),
			);
			const head = Array.from(bytes, (byte) =>
				byte.toString(16).padStart(2, '0'),
			).join('');
			throw new Error(
				`Native screenshot decode failed: status=${response.status} type=${
					blob.type || 'unknown'
				} bytes=${blob.size} head=${head}`,
			);
		}
		const scale = Math.min(1, 960 / image.width, 540 / image.height);
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(image.width * scale);
		canvas.height = Math.round(image.height * scale);
		const context = canvas.getContext('2d');
		if (!context) {
			image.close();
			throw new Error('Canvas is unavailable');
		}
		context.drawImage(image, 0, 0, canvas.width, canvas.height);
		image.close();
		if (signal.aborted) throw new Error('Screenshot capture timed out');

		let dataUrl = canvas.toDataURL('image/jpeg', 0.6);
		if (dataUrl.length > 450_000) {
			dataUrl = canvas.toDataURL('image/jpeg', 0.35);
		}
		if (dataUrl.length > 450_000) {
			throw new Error('Screenshot is too large');
		}
		return dataUrl;
	}

	@notifyOnError
	public async markModerationRequestsViewed(
		payload: IAdminMarkModerationRequestsViewed,
	): Promise<void> {
		await this.backend.invoke('admin:markModerationRequestsViewed', payload);
	}
}
